import React, { useState } from "react";
import { Check } from "lucide-react";
import axios from "../config/axios";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";

/**
 * Paywall Component
 * Shows pricing and features comparison, handles upgrade flow
 */
const Paywall = ({ onClose, className = "", currentPlan = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      name: "Free",
      price: "$0",
      interval: "",
      current: currentPlan === "free" || currentPlan === "Free",
      badge: null,
      bgColor: "bg-white",
      subtitle: "1 Analysis",
      features: [
        "Basic CRO Analysis",
        "Performance Metrics",
        "Top 5 Recommendations",
        "Overall Score",
      ],
      cta: "Free",
      ctaDisabled: true,
    },
    {
      name: "Standard",
      price: "$99",
      interval: "/mo",
      current: currentPlan === "standard" || currentPlan === "Standard",
      recommended: true,
      badge: "Save 60%",
      badgeColor: "bg-green-500",
      bgColor: "bg-gradient-to-br from-blue-500 to-blue-600",
      textColor: "text-white",
      subtitle: "10 Analyses/month",
      features: [
        "Everything in Free",
        "Up to 10 CRO Analyses/month",
        "Advanced Recommendations",
        "Export to PDF",
        "Competitor Analysis (5 sites)",
        "Email Support",
        "14-Day Money-Back Guarantee",
      ],
      cta: "Get Started Now",
    },
    {
      name: "Pro",
      price: "$199",
      interval: "/mo",
      current: currentPlan === "pro" || currentPlan === "Pro",
      recommended: false,
      badge: "Save 64%",
      badgeColor: "bg-green-500",
      bgColor: "bg-white",
      textColor: "text-gray-900",
      subtitle: "Unlimited",
      features: [
        "Everything in Standard",
        "Unlimited CRO Analyses",
        "Full Performance Metrics",
        "Export to PDF/CSV",
        "Competitor Analysis (10 sites)",
        "Conversion Funnel Analysis",
        "A/B Testing Suggestions",
        "Technical SEO Audit",
        "Priority Support",
        "API Access",
        "Historical Tracking",
        "14-Day Money-Back Guarantee",
      ],
      cta: "Get Started Now",
    },
  ];

  const handleUpgrade = async (plan) => {
    if (plan.ctaDisabled) return;

    if (!user) {
      toast.error("Please sign in to upgrade");
      return;
    }

    if (plan.name === "Standard" || plan.name === "Pro") {
      setLoading(true);
      try {
        const token = await user.getIdToken();
        const priceId =
          plan.name === "Pro"
            ? process.env.REACT_APP_STRIPE_PRO_PRICE_ID
            : process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID;

        // Check if user already has an active subscription (upgrade vs new subscription)
        if (currentPlan && currentPlan !== "free" && currentPlan !== "Free") {
          // User is upgrading/changing plan
          const response = await axios.post(
            "/api/subscription/upgrade",
            {
              priceId: priceId,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          toast.success(response.data.message || "Plan upgraded successfully!");

          // Refresh the page after a short delay
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          // New subscription - use checkout
          const response = await axios.post(
            "/api/subscription/checkout",
            {
              priceId: priceId,
              successUrl: `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: `${window.location.origin}/subscription`,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          // Redirect to Stripe checkout
          window.location.href = response.data.checkoutUrl;
        }
      } catch (error) {
        console.error("Upgrade error:", error);
        toast.error(
          error.response?.data?.error?.message || "Failed to process request",
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={`${className}`}>
      {/* Main pricing grid */}
      <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-indigo-50 rounded-3xl p-8">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {plans.map((plan, index) => {
            const isPro = plan.recommended;
            const textColor = plan.textColor || "text-gray-900";
            const secondaryTextColor = isPro
              ? "text-blue-100"
              : "text-gray-600";

            return (
              <div
                key={index}
                className={`relative rounded-2xl p-6 ${plan.bgColor} ${isPro ? "scale-105 shadow-2xl" : "shadow-lg"}`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 right-4">
                    <span
                      className={`${plan.badgeColor} text-white px-3 py-1 rounded-full text-xs font-semibold`}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Current Plan badge */}
                {plan.current && (
                  <div className="absolute -top-3 left-4">
                    <span className="bg-gray-700 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      CURRENT PLAN
                    </span>
                  </div>
                )}

                {/* Recommended badge */}
                {plan.recommended && !plan.current && (
                  <div className="absolute -top-3 left-4">
                    <span className="bg-blue-700 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      RECOMMENDED
                    </span>
                  </div>
                )}

                {/* Plan name */}
                <div className="mb-4">
                  <h3
                    className={`text-sm font-medium ${secondaryTextColor} mb-2`}
                  >
                    {plan.name}
                  </h3>
                  <div className={`flex items-baseline ${textColor}`}>
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className={`text-lg ml-1 ${secondaryTextColor}`}>
                      {plan.interval}
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleUpgrade(plan)}
                  disabled={loading || plan.ctaDisabled || plan.current}
                  className={`w-full py-3 px-4 rounded-xl font-semibold mb-6 transition-all ${
                    plan.current
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : isPro
                        ? "bg-white text-blue-600 hover:bg-blue-50"
                        : plan.ctaDisabled
                          ? "bg-blue-600 text-white cursor-default"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                  } disabled:opacity-50`}
                >
                  {loading
                    ? "Processing..."
                    : plan.current
                      ? "Current Plan"
                      : plan.cta}
                </button>

                {/* Subtitle */}
                {plan.subtitle && (
                  <div
                    className={`mb-4 pb-4 border-b ${isPro ? "border-blue-400" : "border-gray-200"}`}
                  >
                    <p className={`font-bold text-lg ${textColor}`}>
                      {plan.subtitle}
                    </p>
                  </div>
                )}

                {/* Features */}
                <div>
                  <p className={`font-semibold text-sm ${textColor} mb-3`}>
                    Features
                  </p>
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check
                          className={`w-4 h-4 mr-2 flex-shrink-0 mt-0.5 ${isPro ? "text-white" : "text-blue-600"}`}
                        />
                        <span className={`text-sm ${textColor}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Enterprise Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-2 text-gray-900">
                Enterprise
              </h3>
              <p className="text-gray-600 mb-6">
                Custom solution for large teams
              </p>

              <h4 className="font-semibold mb-3 text-gray-900">Features</h4>
              <ul className="space-y-2">
                {[
                  "Everything in Pro",
                  "Unlimited Team Members",
                  "Unlimited Competitor Analysis",
                  "Custom Integrations",
                  "Dedicated Account Manager",
                  "SLA Guarantee",
                  "Training & Onboarding",
                  "Custom Reports",
                  "White-label Options",
                  "On-premise Deployment",
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-blue-600" />
                    <span className="text-sm text-gray-900">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-center">
              <button
                onClick={() =>
                  (window.location.href =
                    "mailto:sales@partnerly.com?subject=Enterprise Plan Inquiry")
                }
                className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Close button if in modal */}
      {onClose && (
        <button
          onClick={onClose}
          className="mt-6 w-full py-3 text-gray-600 hover:text-gray-900 font-medium"
        >
          Maybe Later
        </button>
      )}
    </div>
  );
};

export default Paywall;
